import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { MapPin, Save, Trash2, Loader2, RefreshCw, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PinakioBranchMappingTab() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ mappings: [], distinct_prefixes: [], branches: [], case_counts_per_prefix: {} });
  const [edits, setEdits] = useState({});  // {prefix: {branch, label}}
  const [savingPrefix, setSavingPrefix] = useState(null);
  const [newBranch, setNewBranch] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/pinakio-branches`);
      setData(r.data);
      // Pre-fill edits from existing mappings
      const initial = {};
      for (const m of (r.data.mappings || [])) {
        initial[m.prefix] = {
          branch: m.branch,
          label: m.label || m.prefix,
          is_regional: !!m.is_regional,
          tsd_act_number: m.tsd_act_number || "",
          regional_sector_number: m.regional_sector_number || "",
        };
      }
      setEdits(initial);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setEdit = (prefix, key, value) => {
    setEdits(prev => ({ ...prev, [prefix]: { ...(prev[prefix] || {}), [key]: value } }));
  };

  const saveMapping = async (prefix) => {
    const e = edits[prefix] || {};
    if (!e.branch) { toast.error("Επιλέξτε κατάστημα"); return; }
    try {
      setSavingPrefix(prefix);
      await axios.put(`${API}/admin/pinakio-branches/${encodeURIComponent(prefix)}`, {
        branch: e.branch,
        label: e.label || prefix,
        is_regional: !!e.is_regional,
        tsd_act_number: (e.tsd_act_number || "").trim(),
        regional_sector_number: e.regional_sector_number || null,
      });
      toast.success(`Αποθηκεύτηκε: ${prefix} → ${e.branch}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Σφάλμα αποθήκευσης");
    } finally { setSavingPrefix(null); }
  };

  const deleteMapping = async (prefix) => {
    if (!window.confirm(`Διαγραφή αντιστοίχισης για το πρόθεμα "${prefix}";`)) return;
    try {
      await axios.delete(`${API}/admin/pinakio-branches/${encodeURIComponent(prefix)}`);
      toast.success("Διαγράφηκε");
      setEdits(prev => { const n = {...prev}; delete n[prefix]; return n; });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Σφάλμα διαγραφής");
    }
  };

  const mappingByPrefix = Object.fromEntries((data.mappings || []).map(m => [m.prefix, m]));
  // Include branches από: (a) χρήστες (b) ήδη αποθηκευμένα mappings (c) νέο που πληκτρολογεί ο admin
  // Αυτό λύνει το bug όπου mapping σε branch που δεν έχει user → dropdown εμφανίζεται κενό.
  const mappedBranches = (data.mappings || []).map(m => m.branch).filter(Boolean);
  const branchOptions = [...new Set([
    ...(data.branches || []),
    ...mappedBranches,
    ...(newBranch ? [newBranch] : []),
  ])];

  return (
    <Card data-testid="pinakio-branch-mapping-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-blue-600" />
          Αντιστοίχιση Πινακίων ↔ Καταστημάτων
        </CardTitle>
        <CardDescription className="space-y-1">
          <p>
            Κάθε <b>πρόθεμα πινακίου</b> (π.χ. ΠΕΡ, ΚΑΛ, ΗΑΣ) αντιστοιχεί σε ένα κατάστημα (περιφερειακή έδρα).
            Όταν ο χρήστης συνδέεται με συγκεκριμένο κατάστημα, βλέπει μόνο τα πινάκια του.
          </p>
          <div className="flex items-start gap-2 mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Παραδείγματα: <b>ΠΕΡ</b> → Περιστερίου, <b>ΚΑΛ</b> → Καλλιθέας, <b>ΑΜΑ</b> → Αμαρουσίου.
            Τα <b>distinct_prefixes</b> εξάγονται αυτόματα από το πεδίο <code className="bg-blue-100 px-1">last_hearing_date</code> των υποθέσεων.</span>
          </div>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-3 gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="pbm-reload-btn">
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Ανανέωση
          </Button>
          <div className="flex items-center gap-2">
            <Input
              type="text" placeholder="Προσθήκη νέου καταστήματος (π.χ. Περιστερίου)"
              value={newBranch} onChange={e => setNewBranch(e.target.value)}
              className="text-xs h-8 w-72"
              data-testid="pbm-new-branch-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100">
                  <th className="p-2 text-left">Πρόθεμα</th>
                  <th className="p-2 text-left">Ονομασία</th>
                  <th className="p-2 text-left">Κατάστημα (Branch)</th>
                  <th className="p-2 text-center" title="Περιφερειακή έδρα — διαφορετική υπογραφή στην Πράξη">Περ. Έδρα;</th>
                  <th className="p-2 text-left" title="ΤΣΔ Εξουσιοδότησης ανά Περιφερειακή Έδρα (παράγρ. Γ)">ΤΣΔ Έδρας</th>
                  <th className="p-2 text-center" title="Αριθμός τμήματος προσδιορισμού περιφερειακής έδρας (πχ 3 για 3ο Ενοχικού-Εμπορικού)">Τμήμα Προσδ.</th>
                  <th className="p-2 text-center"># Υποθέσεις</th>
                  <th className="p-2 text-center">Κατάσταση</th>
                  <th className="p-2 text-right">Ενέργειες</th>
                </tr>
              </thead>
              <tbody>
                {(data.distinct_prefixes || []).map(prefix => {
                  const existing = mappingByPrefix[prefix];
                  const e = edits[prefix] || {};
                  const cnt = data.case_counts_per_prefix?.[prefix] || 0;
                  const isMapped = !!existing;
                  const isRegional = e.is_regional ?? existing?.is_regional ?? false;
                  return (
                    <tr key={prefix} className={`border-t ${isMapped ? 'bg-green-50/40' : ''}`} data-testid={`pbm-row-${prefix}`}>
                      <td className="p-2 font-mono font-semibold text-blue-900">{prefix}</td>
                      <td className="p-2">
                        <Input
                          type="text" placeholder={`π.χ. ${prefix === 'ΠΕΡ' ? 'Περιστερίου' : prefix === 'ΚΑΛ' ? 'Καλλιθέας' : 'Ονομασία'}`}
                          value={e.label ?? existing?.label ?? prefix}
                          onChange={ev => setEdit(prefix, 'label', ev.target.value)}
                          className="text-xs h-8"
                          data-testid={`pbm-label-${prefix}`}
                        />
                      </td>
                      <td className="p-2">
                        <Select
                          value={e.branch ?? existing?.branch ?? ""}
                          onValueChange={v => {
                            setEdit(prefix, 'branch', v);
                            // Auto-fill label με το πλήρες όνομα έδρας ώστε να εμφανίζεται σωστά
                            // στις Πράξεις Ορισμού.
                            const currentLabel = (edits[prefix]?.label ?? mappingByPrefix[prefix]?.label ?? "").trim();
                            if (!currentLabel || currentLabel === prefix) {
                              setEdit(prefix, 'label', v);
                            }
                          }}
                        >
                          <SelectTrigger className="text-xs h-8 w-52" data-testid={`pbm-branch-${prefix}`}>
                            <SelectValue placeholder="Επιλέξτε κατάστημα..." />
                          </SelectTrigger>
                          <SelectContent>
                            {branchOptions.map(b => (
                              <SelectItem key={b} value={b}>{b}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2 text-center">
                        <input
                          type="checkbox"
                          checked={isRegional}
                          onChange={ev => setEdit(prefix, 'is_regional', ev.target.checked)}
                          className="w-4 h-4 cursor-pointer accent-amber-600"
                          data-testid={`pbm-regional-${prefix}`}
                          title="Περιφερειακή έδρα (Καλλιθέας/Κορωπίου) — διαφορετική υπογραφή"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="text" placeholder={isRegional ? "π.χ. 8/2026" : "—"}
                          value={e.tsd_act_number ?? existing?.tsd_act_number ?? ""}
                          onChange={ev => setEdit(prefix, 'tsd_act_number', ev.target.value)}
                          disabled={!isRegional}
                          className="text-xs h-8 w-28 disabled:bg-slate-100 disabled:text-slate-400"
                          data-testid={`pbm-tsd-${prefix}`}
                        />
                      </td>
                      <td className="p-2 text-center">
                        <Input
                          type="number" min="1" max="20" placeholder={isRegional ? "3" : "—"}
                          value={e.regional_sector_number ?? existing?.regional_sector_number ?? ""}
                          onChange={ev => setEdit(prefix, 'regional_sector_number', ev.target.value ? parseInt(ev.target.value, 10) : "")}
                          disabled={!isRegional}
                          className="text-xs h-8 w-16 mx-auto disabled:bg-slate-100 disabled:text-slate-400"
                          data-testid={`pbm-regional-sector-${prefix}`}
                          title="Αριθμός τμήματος που προσδιορίζει τις υποθέσεις της περ. έδρας (πχ 3 για Ενοχικού-Εμπορικού)"
                        />
                      </td>
                      <td className="p-2 text-center text-xs font-mono">{cnt.toLocaleString('el-GR')}</td>
                      <td className="p-2 text-center">
                        {isMapped ? (
                          isRegional ? (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-300">Περιφερειακή</Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Αντιστοιχισμένο</Badge>
                          )
                        ) : (
                          <Badge variant="outline" className="text-amber-700 border-amber-300">Εκκρεμεί</Badge>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm" variant="outline"
                            className="h-7 text-xs"
                            disabled={savingPrefix === prefix}
                            onClick={() => saveMapping(prefix)}
                            data-testid={`pbm-save-${prefix}`}
                          >
                            {savingPrefix === prefix ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                            Αποθήκευση
                          </Button>
                          {isMapped && (
                            <Button
                              size="icon" variant="ghost"
                              className="h-7 w-7 text-red-600 hover:bg-red-50"
                              onClick={() => deleteMapping(prefix)}
                              data-testid={`pbm-delete-${prefix}`}
                              title="Διαγραφή αντιστοίχισης"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {(data.distinct_prefixes || []).length === 0 && (
                  <tr><td colSpan={9} className="p-8 text-center text-slate-400">Δεν βρέθηκαν προθέματα πινακίων.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 text-xs text-slate-500">
          Σύνολο προθεμάτων: <b>{data.distinct_prefixes?.length || 0}</b> ·
          Αντιστοιχισμένα: <b className="text-emerald-700">{data.mappings?.length || 0}</b> ·
          Εκκρεμή: <b className="text-amber-700">{(data.distinct_prefixes?.length || 0) - (data.mappings?.length || 0)}</b>
        </div>
      </CardContent>
    </Card>
  );
}
